/**
 * @Module: API Service
 * @Status: Ready
 * @Summary: API service for connecting to the CHOPPY backend
 * @Description: Provides methods for interacting with the CHOPPY API endpoints.
 *   Auth is handled by Firebase (see contexts/AuthContext). Authenticated
 *   requests inject a Firebase ID token via getAuthHeaders().
 * @NextSteps: Implement grocery list endpoints when backend is ready
 */

import { util_log_debug_01 } from '../utils/logging';
import { auth } from '../config/firebase';
import type { KitchenProfile } from '../types/kitchen';
import { profileToApi } from '../types/kitchen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_PREFIX = '/api';

/** Build headers including BYO API key if present. */
function getAIHeaders(): Record<string, string> {
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	try {
		const byoKey = localStorage.getItem('choppy:byoApiKey');
		if (byoKey) headers['X-User-API-Key'] = byoKey;
	} catch { /* ignore */ }
	return headers;
}

// Types — must match backend UpdatePreferencesRequest in user_models_01.py
export interface UserPreferences {
	dietary_restrictions: string[];
	cooking_skill_level: number;
	favorite_cuisines: string[];
	max_prep_time_minutes: number;
	household_size: number;
}

export interface Recipe {
	id: string;
	title: string;
	ingredients: {
		quantity: string;
		unit: string;
		name: string;
		notes?: string;
	}[];
	steps: string[];
	prep_time_minutes: number;
	servings: number;
}

export interface GroceryList {
	id: string;
	items: string[];
	recipe_id?: string;
}

// Firebase token injection for authenticated requests
async function getAuthHeaders(): Promise<Record<string, string>> {
	if (!auth) return {};
	const user = auth.currentUser;
	if (!user) return {};
	const token = await user.getIdToken();
	return { Authorization: `Bearer ${token}` };
}

// API Service
export const api = {
	// Recipe endpoints
	recipes: {
		async list(): Promise<Recipe[]> {
			util_log_debug_01('Step 1: Fetching recipes');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes`, {
				headers: { ...authHeaders },
			});

			if (!response.ok) throw new Error('Failed to fetch recipes');
			return response.json();
		},

		async generate(preferences: Partial<UserPreferences>): Promise<Recipe> {
			util_log_debug_01('Step 1: Generating recipe');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/generate`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...authHeaders,
				},
				body: JSON.stringify(preferences),
			});

			if (!response.ok) throw new Error('Failed to generate recipe');
			return response.json();
		},

		async scale(recipeId: string, servings: number): Promise<Recipe> {
			util_log_debug_01('Step 1: Scaling recipe');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/${recipeId}/scale`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...authHeaders,
				},
				body: JSON.stringify({ servings }),
			});

			if (!response.ok) throw new Error('Failed to scale recipe');
			return response.json();
		}
	},

	// User preferences endpoints
	preferences: {
		async get(userId: string): Promise<UserPreferences> {
			util_log_debug_01('Step 1: Fetching user preferences');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/users/preferences/${userId}`, {
				headers: { ...authHeaders },
			});

			if (!response.ok) throw new Error('Failed to fetch preferences');
			return response.json();
		},

		async update(userId: string, preferences: Partial<UserPreferences>): Promise<UserPreferences> {
			util_log_debug_01('Step 1: Updating user preferences');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/users/preferences/${userId}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					...authHeaders,
				},
				body: JSON.stringify(preferences),
			});

			if (!response.ok) throw new Error('Failed to update preferences');
			return response.json();
		}
	},

	// Recipe parsing (public — no auth required)
	parse: {
		async parseRecipe(text: string, smart: boolean = false, kitchenContext?: string): Promise<any> {
			const endpoint = smart ? '/recipes/parse/smart' : '/recipes/parse';
			util_log_debug_01(`Step 1: Sending recipe text to ${smart ? 'LLM' : 'heuristic'} parser`);
			const body: Record<string, any> = { text };
			if (kitchenContext) {
				body.kitchen_context = kitchenContext;
			}
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}${endpoint}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Parse failed' }));
				throw new Error(error.detail || `Parse failed (${response.status})`);
			}
			return response.json();
		}
	},

	// Kitchen profile (public — no auth required)
	kitchen: {
		async parse(text: string): Promise<any> {
			util_log_debug_01('Step 1: Sending kitchen description to parser');
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/kitchen/parse`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ text }),
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Parse failed' }));
				throw new Error(error.detail || `Kitchen parse failed (${response.status})`);
			}
			return response.json();
		},

		async save(profile: KitchenProfile): Promise<void> {
			util_log_debug_01('Step 1: Saving kitchen profile');
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/kitchen/save`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(profileToApi(profile)),
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Save failed' }));
				throw new Error(error.detail || `Kitchen save failed (${response.status})`);
			}
		},

		async get(): Promise<any | null> {
			util_log_debug_01('Step 1: Fetching kitchen profile');
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/kitchen/profile`);

			if (!response.ok) {
				if (response.status === 404) return null;
				const error = await response.json().catch(() => ({ detail: 'Fetch failed' }));
				throw new Error(error.detail || `Kitchen fetch failed (${response.status})`);
			}
			return response.json();
		},
	},

	// Recipe generation (public — no auth required)
	generate: {
		async generateRecipe(
			prompt: string,
			kitchenContext?: string,
			availableIngredients?: string[],
		): Promise<any> {
			util_log_debug_01('Step 1: Sending generation prompt to backend');
			const body: Record<string, any> = { prompt };
			if (kitchenContext) {
				body.kitchen_context = kitchenContext;
			}
			if (availableIngredients?.length) {
				body.available_ingredients = availableIngredients;
			}
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/generate`, {
				method: 'POST',
				headers: getAIHeaders(),
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Generation failed' }));
				throw new Error(error.detail || `Generation failed (${response.status})`);
			}
			return response.json();
		}
	},

	// Recipe recommendations — lightweight suggestions before full generation
	recommend: {
		async getIdeas(
			availableIngredients: string[],
			preferences: string,
			pantryOnly: boolean,
			count: number,
			kitchenContext?: string,
		): Promise<{ recommendations: Array<{ title: string; description: string; estimated_time: string; estimated_difficulty: string; key_ingredients: string[]; missing_ingredients: string[] }> }> {
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/recommend`, {
				method: 'POST',
				headers: getAIHeaders(),
				body: JSON.stringify({
					available_ingredients: availableIngredients,
					preferences,
					pantry_only: pantryOnly,
					count,
					kitchen_context: kitchenContext,
				}),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Recommendations failed' }));
				throw new Error(error.detail || 'Failed to get recommendations');
			}
			return response.json();
		},
	},

	// Recipe verification — web search for similar real recipes
	verify: {
		async searchSimilar(title: string, keyIngredients: string[]): Promise<any> {
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/verify`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ title, key_ingredients: keyIngredients }),
			});
			if (!response.ok) return { similar_recipes: [], confidence: 'low' };
			return response.json();
		}
	},

	// Web recipe fetching — extract recipe summaries from URLs
	fetch: {
		async fetchSummary(url: string): Promise<any> {
			util_log_debug_01(`Step 1: Fetching recipe summary from ${url.slice(0, 60)}`);
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/fetch-summary`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ url }),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Fetch failed' }));
				throw new Error(error.detail || `Fetch failed (${response.status})`);
			}
			return response.json();
		},

		async fetchBatch(urls: string[]): Promise<any> {
			util_log_debug_01(`Step 1: Batch-fetching ${urls.length} recipe URLs`);
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/fetch-batch`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ urls }),
			});
			if (!response.ok) return { recipes: [] };
			return response.json();
		},
	},

	// Recipe refinement — improve recipes with feedback + web references
	refine: {
		async refineRecipe(
			original: any,
			feedback: string,
			referenceRecipes: any[] = [],
			kitchenContext?: string,
		): Promise<any> {
			util_log_debug_01('Step 1: Sending refinement request');
			const body: Record<string, any> = {
				original,
				feedback,
				reference_recipes: referenceRecipes,
			};
			if (kitchenContext) {
				body.kitchen_context = kitchenContext;
			}
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/refine`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Refinement failed' }));
				throw new Error(error.detail || `Refinement failed (${response.status})`);
			}
			return response.json();
		},
	},

	// Recipe Q&A — ask questions without refinement
	qa: {
		async askAboutRecipe(
			question: string,
			recipe: any,
			kitchenContext?: string,
		): Promise<{ answer: string }> {
			const body: Record<string, any> = { question, recipe };
			if (kitchenContext) body.kitchen_context = kitchenContext;
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/ask`, {
				method: 'POST',
				headers: getAIHeaders(),
				body: JSON.stringify(body),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Q&A failed' }));
				throw new Error(error.detail || `Q&A failed (${response.status})`);
			}
			return response.json();
		},
	},

	// Meal plan endpoints
	mealPlan: {
		async generate(prompt: string, numRecipes: number, constraints: any): Promise<any> {
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/meal-plans/generate`, {
				method: 'POST',
				headers: getAIHeaders(),
				body: JSON.stringify({ prompt, num_recipes: numRecipes, constraints }),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Generation failed' }));
				throw new Error(error.detail || 'Meal plan generation failed');
			}
			return response.json();
		},
		async optimizePrep(recipes: any[], batchCookDay?: string): Promise<any> {
			// Backend expects { recipe_titles, recipe_steps, batch_cook_day }
			const recipe_titles = recipes.map((r: any) => r.title || 'Untitled');
			const recipe_steps = recipes.map((r: any) =>
				(r.steps || []).map((s: any) => ({
					number: s.number,
					instruction: s.instruction,
					ingredients: (s.ingredients || []).map((i: any) => ({
						name: i.name,
						amount: i.amount,
						unit: i.unit,
						notes: i.notes || '',
					})),
					durationMinutes: s.durationMinutes ?? null,
					technique: s.technique ?? null,
					title: s.title ?? null,
				}))
			);

			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/meal-plans/optimize-prep`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ recipe_titles, recipe_steps, batch_cook_day: batchCookDay }),
			});
			if (!response.ok) {
				const error = await response.json().catch(() => ({ detail: 'Prep optimization failed' }));
				throw new Error(error.detail || 'Failed to optimize prep schedule');
			}
			const steps = await response.json();
			// Backend returns List[ScheduleStepResponse] directly, wrap for frontend
			return { prep_steps: Array.isArray(steps) ? steps : [] };
		},
	},

	// Grocery list endpoints
	groceryList: {
		async createFromRecipe(recipeId: string): Promise<GroceryList> {
			util_log_debug_01('Step 1: Creating grocery list from recipe');
			const authHeaders = await getAuthHeaders();
			const response = await fetch(`${API_BASE_URL}${API_PREFIX}/recipes/${recipeId}/grocery-list`, {
				method: 'POST',
				headers: { ...authHeaders },
			});

			if (!response.ok) throw new Error('Failed to create grocery list');
			return response.json();
		}
	}
};


